import type { SetState } from "zustand";

import type { EditableGoalFields, GoalSuggestion, GoalSuggestionState } from "../../types";
import type { AskPerformYardState } from "../types";

import { persistSuggestionOutcome } from "../helpers";

export const createGoalSlice = (set: SetState<AskPerformYardState>) => ({
	goalSuggestionStates: [] as GoalSuggestionState[],
	editedGoalFields: {} as Record<string, EditableGoalFields>,
	creatingSuggestionIds: new Set<string>(),
	isCreatingAll: false,
	validationTrigger: 0,
	isGoalEditPanelOpen: false,
	activeEditMessageId: null as string | null,

	setGoalSuggestions: (messageId: string, suggestions: GoalSuggestion[]) => {
		set((state) => {
			// Attach suggestions to the message
			const msgIdx = state.messages.findIndex((m) => m.id === messageId);
			const updatedMessages = state.messages.slice();
			if (msgIdx !== -1) {
				updatedMessages[msgIdx] = { ...updatedMessages[msgIdx], goalSuggestions: suggestions };
			}

			// Create suggestion states tagged with messageId
			const newStates: GoalSuggestionState[] = suggestions.map((s) => ({
				suggestion: s,
				status: "pending" as const,
				messageId,
			}));

			// Replace any existing states for this messageId; keep states from other messages
			const otherStates = state.goalSuggestionStates.filter((gs) => gs.messageId !== messageId);

			return {
				messages: updatedMessages,
				goalSuggestionStates: [...otherStates, ...newStates],
			};
		});
	},

	setEditedGoalFields: (suggestionId: string, fields: EditableGoalFields) => {
		set((state) => ({
			editedGoalFields: { ...state.editedGoalFields, [suggestionId]: fields },
		}));
	},

	markGoalCreated: (suggestionId: string, goalId: string, edits?: Partial<GoalSuggestion>) => {
		// Durable, not just in-memory — see helpers.ts. Guards against recreating the
		// same goal after a reload or in a fresh tab, where in-memory state is gone.
		persistSuggestionOutcome(suggestionId, { kind: "goal", createdGoalId: goalId });
		set((state) => {
			const updated = state.goalSuggestionStates.map((gs) => {
				if (gs.suggestion.id !== suggestionId) return gs;
				return {
					...gs,
					status: "created" as const,
					createdGoalId: goalId,
					suggestion: edits ? { ...gs.suggestion, ...edits } : gs.suggestion,
				};
			});

			// Also update the suggestion in the message's goalSuggestions array
			const updatedMessages = edits
				? state.messages.map((msg) => {
						if (!msg.goalSuggestions) return msg;
						const idx = msg.goalSuggestions.findIndex((s) => s.id === suggestionId);
						if (idx === -1) return msg;
						const updatedSuggestions = msg.goalSuggestions.slice();
						updatedSuggestions[idx] = { ...updatedSuggestions[idx], ...edits };
						return { ...msg, goalSuggestions: updatedSuggestions };
					})
				: state.messages;

			return {
				goalSuggestionStates: updated,
				messages: updatedMessages,
			};
		});
	},

	markGoalDeleted: (suggestionId: string) => {
		set((state) => {
			const updated = state.goalSuggestionStates.map((gs) =>
				gs.suggestion.id === suggestionId ? { ...gs, status: "deleted" as const } : gs
			);
			return {
				goalSuggestionStates: updated,
			};
		});
	},

	markGoalCreating: (suggestionId: string) => {
		set((state) => ({
			creatingSuggestionIds: new Set(state.creatingSuggestionIds).add(suggestionId),
		}));
	},

	clearGoalCreating: (suggestionId: string) => {
		set((state) => {
			const next = new Set(state.creatingSuggestionIds);
			next.delete(suggestionId);
			return { creatingSuggestionIds: next };
		});
	},

	setIsCreatingAll: (value: boolean) => set({ isCreatingAll: value }),
	triggerValidation: () => set((state) => ({ validationTrigger: state.validationTrigger + 1 })),

	openGoalEditPanel: (messageId: string) => set({ isGoalEditPanelOpen: true, activeEditMessageId: messageId }),
	closeGoalEditPanel: () => set({ isGoalEditPanelOpen: false, activeEditMessageId: null }),
});
